import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { useNavigate, Link } from "react-router-dom";
import {
  postRequest,
  setUserEmail,
  setUserType,
  updateUser,
  hideModal,
  showModalFunc,
} from "../../../redux/action";
import SignUpFormBase from "./signUpForm";
import { logoWhiteImg } from "../../../images";
import styles from "./signUp.module.css";
import { storePendingVerificationEmail, storePendingVerificationUserType } from "../../../utils/verificationEmail";
import { storeOriginalRegisteredRole } from "../../../utils/originalRegisteredRole";

const duplicateRegistrationMessages = {
  student:
    "This email is already used to register as a student. Please log in with your student account or reset your password.",
  mentor:
    "This email is already used to register as a mentor. Please log in with your mentor account or reset your password.",
};

const getDuplicateRegistrationMessage = (errorMsg = "") => {
  if (/mentor/i.test(errorMsg)) {
    return duplicateRegistrationMessages.mentor;
  }

  if (/student/i.test(errorMsg)) {
    return duplicateRegistrationMessages.student;
  }

  return duplicateRegistrationMessages.mentor;
};

const isDuplicateRegistrationError = (errorMsg = "") =>
  /already taken|already used|already registered|email is already taken|email already exists/i.test(
    errorMsg,
  );

const MentorSignUp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setError,
    formState: { errors, isDirty, isValid },
  } = useForm({ mode: "all", defaultValues: { phone_number: "+1" } });

  const passwordValue = watch("password", "");

  const handleShowPassword = () => setShowPassword(!showPassword);

  const handleLoginClick = () => {
    dispatch(hideModal());
    dispatch(showModalFunc("mentor"));
  };

  const onSubmit = async (formData) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const [success, responseData] = await postRequest(
        "/api/auth/local/mentor/register",
        {
          username: formData.username || formData.email,
          firstname: formData.firstname,
          lastname: formData.lastname,
          email: formData.email,
          password: formData.password,
          phoneNumber: formData.phone_number,
          location: formData.location,
          country: formData.country,
          role: "mentor",
          originalRegisteredRole: "mentor",
        },
      );

      if (!success || responseData?.error) {
        const errorMsg =
          responseData?.error?.message ||
          "An error occurred during registration.";
        if (
          responseData?.error?.status === 500 ||
          isDuplicateRegistrationError(errorMsg)
        ) {
          const message = getDuplicateRegistrationMessage(errorMsg);
          setError("email", { type: "server", message });
          toast.error(message, { autoClose: 3000 });
        } else if (
          errorMsg.includes("email") ||
          errorMsg.toLowerCase().includes("email")
        ) {
          setError("email", { type: "server", message: errorMsg });
          toast.error(errorMsg, { autoClose: 2000 });
        } else if (
          errorMsg.includes("password") ||
          errorMsg.toLowerCase().includes("password")
        ) {
          setError("password", { type: "server", message: errorMsg });
          toast.error(errorMsg, { autoClose: 2000 });
        } else if (
          errorMsg.includes("phone") ||
          errorMsg.toLowerCase().includes("phone")
        ) {
          setError("phone_number", { type: "server", message: errorMsg });
          toast.error(errorMsg, { autoClose: 2000 });
        } else {
          toast.error(errorMsg, { autoClose: 2000 });
        }
      } else {
        reset();
        storeOriginalRegisteredRole(formData.email, "mentor");
        const userEmail = storePendingVerificationEmail(formData.email);
        dispatch(setUserEmail(userEmail));
        dispatch(setUserType("mentor"));
        storePendingVerificationUserType("mentor");
        dispatch(updateUser(formData));
        navigate("/mentor-verify-email");
        toast.success(
          "Mentor account created successfully! Please verify your email.",
        );
      }
    } catch (error) {
      console.error("Error during mentor sign up:", error);
      toast.error("An error occurred while signing up", { autoClose: 2000 });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="bg-black min-h-screen">
      <div className={styles["authContentModal"]}>
        <div className={styles["headerWrapper"]}>
          <Link to="/" className={styles["imageWrapper"]}>
            <img
              src={logoWhiteImg}
              alt="logo"
              onClick={() => dispatch(hideModal())}
            />
          </Link>
          <h2 className={styles["authHeader"]}>Mentor Sign Up</h2>
          <p className="font-normal text-[12px] text-center leading-[20px] text-[#FCFCF5] tracking-[0] absolute top-[160px]">
            Unlock your potential to empower.
            <br className="block md:hidden" /> Register as a mentor now
          </p>
        </div>

        <div className={styles["signup_wrapper"]}>
          <div className={styles["inner_form"]}>
            <SignUpFormBase
              onSubmit={onSubmit}
              register={register}
              control={control}
              handleSubmit={handleSubmit}
              errors={errors}
              passwordValue={passwordValue}
              isDirty={isDirty}
              isValid={isValid}
              showPassword={showPassword}
              handleShowPassword={handleShowPassword}
              formType="mentor"
              isLoading={isLoading}
            />
            <label className="text-[#D8D8D8] font-normal text-xs leading-5 mt-8">
              Already have an account?{" "}
              <button
                type="button"
                className="underline ml-1 text-[#D8D8D8] bg-transparent border-none cursor-pointer text-xs leading-5"
                onClick={handleLoginClick}
              >
                Log in
              </button>
            </label>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MentorSignUp;
